/**
 * Konstanten des visuellen PDF-Vorlagen-Designers. Backend-Compiler und Canvas-Editor
 * teilen sich diese Werte, damit Vorschau und Druckbild identisch bleiben.
 */
import { DOCUMENT_TYPES } from './documents.js';
/** Belegarten, für die eine Vorlage greifen kann; `ALL` gilt als Rückfallebene. */
export const PDF_TEMPLATE_DOCUMENT_TYPES = [
    'QUOTE',
    'ORDER',
    'DELIVERY_NOTE',
    'INVOICE',
    'PURCHASE_ORDER',
    'ALL',
];
export const PDF_PAGE_FORMATS = ['A4'];
export const PDF_CUSTOM_FIELD_AUTO_WIDTH = 4;
export const PDF_CUSTOM_FIELD_FULL_WIDTH = 12;
export const PDF_CUSTOM_FIELD_LONG_VALUE_LENGTH = 120;
export const PDF_PAGE_ORIENTATIONS = ['PORTRAIT', 'LANDSCAPE'];
/** `Helvetica` und `Times-Roman` sind PDF-Standardschriften, `Inter`/`Roboto` werden eingebettet. */
export const PDF_FONT_FAMILIES = ['Inter', 'Roboto', 'Helvetica', 'Times-Roman'];
export const PDF_BLOCK_TYPES = [
    'LOGO',
    'SENDER_LINE',
    'RECIPIENT_ADDRESS',
    'DOCUMENT_META',
    'HEADER_TEXT',
    'CUSTOMER_HEADER',
    'RECEIVING_HOURS',
    'TRANSPORT_PLAN',
    'ITEMS_TABLE',
    'SHIPPING_SUMMARY',
    'TOTALS_BLOCK',
    'GIROCODE_QR',
    'FOOTER_TEXT',
    'FOOTER_COLUMNS',
    'CUSTOM_HTML_TEXT',
];
/** Reihenfolge bisheriger Belegdaten, wenn eine Vorlage keine eigene Liste speichert. */
export const PDF_DOCUMENT_META_KEYS = [
    'documentNumber',
    'documentDate',
    'customerNumber',
    'contactPerson',
    'reference',
    'serviceDate',
    'deliveryDate',
    'validUntil',
    'dueDate',
];
/**
 * Blöcke, die im DIN-5008-Briefkopf sitzen und deshalb immer absolut positioniert
 * werden; sie nehmen nicht am vertikalen Drag-and-Drop teil.
 */
export const PDF_LETTERHEAD_BLOCK_TYPES = [
    'LOGO',
    'SENDER_LINE',
    'RECIPIENT_ADDRESS',
    'DOCUMENT_META',
];
/** Blöcke, die genau einmal je Vorlage vorkommen dürfen. */
export const PDF_SINGLETON_BLOCK_TYPES = [
    'LOGO',
    'SENDER_LINE',
    'RECIPIENT_ADDRESS',
    'DOCUMENT_META',
    'CUSTOMER_HEADER',
    'RECEIVING_HOURS',
    'TRANSPORT_PLAN',
    'ITEMS_TABLE',
    'SHIPPING_SUMMARY',
    'TOTALS_BLOCK',
    'GIROCODE_QR',
    'FOOTER_COLUMNS',
];
export const PDF_TABLE_COLUMN_KEYS = [
    'position',
    'sku',
    'name',
    'description',
    'batchNumber',
    'expiryDate',
    'quantity',
    'unit',
    'unitPrice',
    'discountPercent',
    'taxRate',
    'lineTotal',
];
/** Spalten, die auf preisfreien Belegen (Lieferschein) unterdrückt werden. */
export const PDF_PRICE_COLUMN_KEYS = [
    'unitPrice',
    'discountPercent',
    'taxRate',
    'lineTotal',
];
export const PDF_TEXT_ALIGNMENTS = ['left', 'center', 'right'];
export const PDF_TEMPLATE_PRESETS = ['CLASSIC_DIN5008', 'MODERN_MINIMALIST', 'COMPACT'];
// ---------------------------------------------------------------------------
// Maße und Grenzen
// ---------------------------------------------------------------------------
export const PDF_PAGE_WIDTH_MM = 210;
export const PDF_PAGE_HEIGHT_MM = 297;
export const PDF_MARGIN_MIN_MM = 5;
export const PDF_MARGIN_MAX_MM = 60;
export const PDF_BASE_FONT_SIZE_MIN = 6;
export const PDF_BASE_FONT_SIZE_MAX = 14;
export const PDF_BLOCK_FONT_SIZE_MIN = 5;
export const PDF_BLOCK_FONT_SIZE_MAX = 32;
export const PDF_LINE_HEIGHT_MIN = 1;
export const PDF_LINE_HEIGHT_MAX = 2;
export const PDF_GIROCODE_SIZE_MIN_MM = 20;
export const PDF_GIROCODE_SIZE_MAX_MM = 60;
export const PDF_LOGO_HEIGHT_MIN_MM = 5;
export const PDF_LOGO_HEIGHT_MAX_MM = 40;
export const PDF_TABLE_COLUMN_MIN_PERCENT = 3;
export const PDF_TABLE_COLUMN_MAX_COUNT = PDF_TABLE_COLUMN_KEYS.length;
export const PDF_TEMPLATE_NAME_MAX_LENGTH = 120;
export const PDF_BLOCK_CONTENT_MAX_LENGTH = 2000;
export const PDF_TEMPLATE_BLOCK_MAX_COUNT = 40;
/** Höchstzahl an Vorlagen je Mandant; verhindert unbeabsichtigtes Zumüllen. */
export const PDF_TEMPLATE_MAX_PER_TENANT = 50;
/** DIN 5008 Form B: Falzmarken und Lochmarke am linken Rand. */
export const DIN5008_FOLD_MARK_TOP_MM = 87;
export const DIN5008_PUNCH_MARK_MM = 148.5;
export const DIN5008_FOLD_MARK_BOTTOM_MM = 192;
/**
 * Storno und Gutschrift teilen sich das Rechnungslayout; für sie existiert bewusst
 * keine eigene Vorlagenart, damit das Druckbild eines Belegkreises einheitlich bleibt.
 */
const DOCUMENT_TYPE_TO_TEMPLATE_TYPE = {
    QUOTE: 'QUOTE',
    ORDER: 'ORDER',
    DELIVERY_NOTE: 'DELIVERY_NOTE',
    INVOICE: 'INVOICE',
    CANCELLATION: 'INVOICE',
    CREDIT_NOTE: 'INVOICE',
    PURCHASE_ORDER: 'PURCHASE_ORDER',
};
export function pdfTemplateDocumentTypeFor(documentType) {
    return DOCUMENT_TYPE_TO_TEMPLATE_TYPE[documentType];
}
/** Belegarten, die eine konkrete Vorlagenart als Beispiel verwenden darf. */
export function documentTypesForTemplateType(templateType) {
    return DOCUMENT_TYPES.filter((type) => templateType === 'ALL' || DOCUMENT_TYPE_TO_TEMPLATE_TYPE[type] === templateType);
}
//# sourceMappingURL=pdf-template.js.map